import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * A row of mutually exclusive choices, all of them visible. Appearance is
 * `.segmented` in `styles/ui.css`.
 *
 * Not `FilterSelect`: these are two to four short words and the whole set fits
 * on one line, so a dropdown would spend a click hiding options the reader can
 * already read. The rule of thumb is the same one the filter bar uses - a
 * dropdown earns its place when the list is long enough to need searching.
 *
 * `aria-pressed` rather than a radio group, because the buttons act immediately
 * rather than staging a value for a submit.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  /** Names the group for a screen reader. "Calendar view", "Which appointments". */
  label: string;
  className?: string;
}) {
  return (
    <div className={cn("segmented", className)} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={cn("segmented__item", value === option.value && "segmented__item--active")}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
